import { ButtonProps } from "@timeedit/ui-components";
import { ReactNode } from "react";
import { Translations, WaitlistButtonProps } from "../types";
import { ButtonWithDescription } from "../ButtonWithDescription";
import { ConflictNotification } from "../../Conflict";
import { IsOnWaitlistButton } from "./IsOnWaitlist";
import WarningOutlined from "@timeedit/ui-components/icons/WarningOutlined";
import SwapOutlined from "@timeedit/ui-components/icons/SwapOutlined";

type AllocatedToOtherProps = {
  onAllocate: () => void;
  waitlistProps: WaitlistButtonProps;
  clash: boolean;
  description: ReactNode;
  linkedIssue: boolean;
  translations: Translations;
  buttonProps: ButtonProps;
  groupName: string;
  activityName: string;
};
export function AllocatedToOther({
  onAllocate,
  waitlistProps,
  clash,
  description,
  linkedIssue,
  translations,
  buttonProps,
  groupName,
  activityName,
}: AllocatedToOtherProps): React.JSX.Element {
  const { isOnWaitlist, onJoinWaitlist, onLeaveWaitlist, waitlistState } =
    waitlistProps;

  if (isOnWaitlist) {
    return (
      <IsOnWaitlistButton
        onLeaveWaitlist={onLeaveWaitlist}
        translations={translations}
        activityName={activityName}
        buttonProps={buttonProps}
        description={description}
        linkedIssue={linkedIssue}
        groupName={groupName}
        clash={clash}
      />
    );
  }

  if (waitlistState.shouldShowWaitlist) {
    return (
      <WaitlistTextButton
        onJoinWaitlist={onJoinWaitlist}
        translations={translations}
        buttonProps={buttonProps}
        description={description}
        linkedIssue={linkedIssue}
        clash={clash}
      />
    );
  }

  return (
    <DefaultAllocateToOtherButton
      translations={translations}
      buttonProps={buttonProps}
      description={description}
      linkedIssue={linkedIssue}
      onAllocate={onAllocate}
      clash={clash}
    />
  );
}

type WaitlistTextButtonProps = {
  buttonProps: ButtonProps;
  description: ReactNode;
  clash: boolean;
  linkedIssue: boolean;
  translations: Translations;
  onJoinWaitlist: () => void;
};
function WaitlistTextButton({
  buttonProps,
  description,
  clash,
  linkedIssue,
  translations,
  onJoinWaitlist,
}: WaitlistTextButtonProps): React.JSX.Element {
  return (
    <>
      <ButtonWithDescription
        {...buttonProps}
        description={description}
        onClick={onJoinWaitlist}
        label={`${translations.addToWaitlist}`}
      >
        {translations.waitlist}
      </ButtonWithDescription>
      {clash && <ConflictNotification useText />}
      {linkedIssue && (
        <WarningOutlined
          className={"allocation-container--icon"}
          alt="Linked issue present"
        />
      )}
    </>
  );
}

type DefaultAllocateToOtherButtonProps = {
  buttonProps: ButtonProps;
  description: ReactNode;
  clash: boolean;
  linkedIssue: boolean;
  translations: Translations;
  onAllocate: () => void;
};

function DefaultAllocateToOtherButton({
  translations,
  buttonProps,
  description,
  linkedIssue,
  onAllocate,
  clash,
}: DefaultAllocateToOtherButtonProps): React.JSX.Element {
  return (
    <>
      <ButtonWithDescription
        {...buttonProps}
        description={description}
        icon={<SwapOutlined aria-hidden />}
        onClick={onAllocate}
        label={translations.swap}
      >
        {translations.swap}
      </ButtonWithDescription>
      {clash && <ConflictNotification useText />}
      {linkedIssue && (
        <WarningOutlined
          className={"allocation-container--icon"}
          alt="Linked issue present"
        />
      )}
    </>
  );
}
